<script lang="ts" setup>
import { GettersTypes, type State } from '@/store/store_types'
import { computed, nextTick, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { useStore } from 'vuex'
import ManagedContentDisplay from './ManagedContentDisplay.vue'

const { mobile } = useDisplay()

type TabType = 'introduction' | 'route planning' | 'see your trip data' | 'connect with our team'
const tab = ref<TabType>('introduction')
const activator = ref(false)

function keyDown(event: KeyboardEvent) {
  if (event.key === 'Escape') activator.value = false
}

function resetScrollPosition() {
  nextTick(() => {
    const myDiv = document.getElementById('dashboard-scroll-container')
    if (myDiv) myDiv.scrollTop = 0
  })
}

const store = useStore<State>()
const fetching = computed(() => store.getters[GettersTypes.fetching])
watch(fetching, (val) => {
  if (!val) {
    if (store.state.DSA_tutorial_wizard) return
    activator.value = true
    resetScrollPosition()
  }
})

const routePlanningContentData = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'Wizard route planning content',
  ),
)
const seeYourTripDataContent = computed(() =>
  store.state.managedContent.find((content) => content.Description === 'Wizard see your trip data'),
)
const connectWithOurTeamContent = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'Wizard connect with our team content',
  ),
)
const introductionContent = computed(() =>
  store.state.managedContent.find((content) => content.Description === 'Wizard introduction'),
)
</script>

<template>
  <v-dialog
    :model-value="activator"
    :width="mobile ? '100%' : '85%'"
    :content-class="
      mobile
        ? 'bg-background pwt-scrollbar-styles'
        : 'rounded-lg bg-background pwt-scrollbar-styles'
    "
    class="pwt-scroll-styles"
    scrollable
    style="z-index: 1200"
    @keydown="keyDown($event)"
    :fullscreen="mobile"
    :max-height="mobile ? undefined : '90vh'"
    :min-height="mobile ? undefined : '90vh'"
  >
    <!-- must have vh for height to work with v-dialog having no exposed height prop. -->
    <v-card
      style="height: 80vh"
      class="position-relative"
      :class="mobile ? 'bg-background overflow-hidden' : 'rounded-lg bg-background overflow-hidden'"
      flat
    >
      <div
        id="dashboard-scroll-container"
        class="pwt-scroll-container pwt-scrollbar-styles overflow-y-auto"
      >
        <!-- second card to counteract miss-behaving v-dialog overflow due to vh needed earlier. -->
        <v-card
          class="pt-0 bg-background"
          :class="mobile ? 'px-0' : 'pr-10 pl-5'"
          flat
          style="position: relative"
        >
          <!-- tabs -->
          <v-tabs
            v-model="tab"
            v-if="!mobile"
            direction="vertical"
            style="position: fixed; top: 40px; width: 200px"
            hide-slider
            @update:model-value="resetScrollPosition"
            optional
            class="pr-5"
          >
            <v-tab
              value="introduction"
              class="text-caption rounded"
              :class="tab === 'introduction' ? 'bg-pwt_azure_20 font-weight-bold' : ''"
            >
              Introduction
            </v-tab>
            <v-tab
              value="route planning"
              class="text-caption rounded"
              :class="tab === 'route planning' ? 'bg-pwt_azure_20 font-weight-bold' : ''"
            >
              {{ routePlanningContentData ? routePlanningContentData.Name : 'Route planning' }}
            </v-tab>
            <v-tab
              value="see your trip data"
              class="text-caption rounded"
              :class="tab === 'see your trip data' ? 'bg-pwt_azure_20 font-weight-bold' : ''"
            >
              {{ seeYourTripDataContent ? seeYourTripDataContent.Name : 'See your trip data' }}
            </v-tab>
            <v-tab
              value="connect with our team"
              class="text-caption rounded"
              :class="tab === 'connect with our team' ? 'bg-pwt_azure_20 font-weight-bold' : ''"
            >
              {{
                connectWithOurTeamContent ? connectWithOurTeamContent.Name : 'Connect with our team'
              }}
            </v-tab>
          </v-tabs>
          <!-- mobile menu tabs -->
          <v-menu v-else>
            <template v-slot:activator="{ props }">
              <v-btn
                icon="mdi-dots-vertical"
                variant="text"
                v-bind="props"
                style="position: fixed; top: 8px; z-index: 1200"
              ></v-btn>
            </template>
            <v-tabs
              v-model="tab"
              direction="vertical"
              hide-slider
              @update:model-value="resetScrollPosition"
              optional
              class="pr-5 bg-background"
            >
              <v-tab
                value="introduction"
                class="text-caption rounded"
                :class="tab === 'introduction' ? 'bg-pwt_azure_20 font-weight-bold' : ''"
              >
                Introduction
              </v-tab>
              <v-tab
                value="route planning"
                class="text-caption rounded"
                :class="tab === 'route planning' ? 'bg-pwt_azure_20 font-weight-bold' : ''"
              >
                {{ routePlanningContentData ? routePlanningContentData.Name : 'Route planning' }}
              </v-tab>
              <v-tab
                value="see your trip data"
                class="text-caption rounded"
                :class="tab === 'see your trip data' ? 'bg-pwt_azure_20 font-weight-bold' : ''"
              >
                {{ seeYourTripDataContent ? seeYourTripDataContent.Name : 'See your trip data' }}
              </v-tab>
              <v-tab
                value="connect with our team"
                class="text-caption rounded"
                :class="tab === 'connect with our team' ? 'bg-pwt_azure_20 font-weight-bold' : ''"
              >
                {{
                  connectWithOurTeamContent
                    ? connectWithOurTeamContent.Name
                    : 'Connect with our team'
                }}
              </v-tab>
            </v-tabs>
          </v-menu>

          <!-- tabs window -->
          <v-tabs-window
            v-model="tab"
            style="min-height: 90vh"
            :style="
              mobile
                ? ''
                : 'margin-left: 200px; border-left: 2px solid rgba(218, 218, 218, 0.5) !important'
            "
            class="bg-background"
            :class="mobile ? 'pa-5 pt-10' : 'py-5 pl-5 pt-8'"
          >
            <v-tabs-window-item value="introduction">
              <ManagedContentDisplay
                :data="introductionContent"
                fallback-title="Welcome to Optimiser Lite!"
              />
            </v-tabs-window-item>
            <v-tabs-window-item value="route planning">
              <ManagedContentDisplay
                :data="routePlanningContentData"
                fallback-title="Route planning"
              />
            </v-tabs-window-item>
            <v-tabs-window-item value="see your trip data">
              <ManagedContentDisplay
                :data="seeYourTripDataContent"
                fallback-title="See your trip data"
              />
            </v-tabs-window-item>
            <v-tabs-window-item value="connect with our team">
              <ManagedContentDisplay
                :data="connectWithOurTeamContent"
                fallback-title="Connect with our team"
              />
            </v-tabs-window-item>
          </v-tabs-window>
        </v-card>
      </div>
      <v-btn icon style="position: absolute; top: 8px; right: 12px" flat color="transparent">
        <v-icon color="pwt_light_grey" @click="activator = false">mdi-close</v-icon>
      </v-btn>
    </v-card>
  </v-dialog>
</template>
